import { useState, useEffect } from "react";
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { BarChart3, LineChart as LineIcon } from "lucide-react";
import { getCargoMonthly } from "@/lib/api";

const SERIES = [
  { key: "import_cargo", name: "Import cargo", color: "#2563eb" },
  { key: "export_cargo", name: "Export cargo", color: "#d97706" },
  { key: "import_hum", name: "Import HUM", color: "#e11d48" },
  { key: "export_hum", name: "Export HUM", color: "#9f1239" },
];

const fmt = (v) => `${Number(v).toLocaleString(undefined, { maximumFractionDigits: 1 })} kg`;

const Toggle = ({ value, onChange, options, testid }) => (
  <div className="inline-flex flex-wrap rounded-md border border-slate-200 bg-white p-0.5" data-testid={testid}>
    {options.map((o) => (
      <button
        key={o.v}
        type="button"
        onClick={() => onChange(o.v)}
        className={`px-2.5 h-8 rounded text-xs font-medium whitespace-nowrap flex items-center gap-1 transition-colors ${value === o.v ? "bg-emerald-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}
        data-testid={`${testid}-${o.v}`}
      >
        {o.icon && <o.icon size={14} />}{o.l}
      </button>
    ))}
  </div>
);

export function CargoMonthlyChart({ year }) {
  const [period, setPeriod] = useState("year");
  const [kind, setKind] = useState("bar");
  const [data, setData] = useState([]);

  useEffect(() => {
    const params = { period };
    if (period === "year") params.year = year;
    getCargoMonthly(params).then((r) => setData(r.data?.months || [])).catch(() => setData([]));
  }, [period, year]);

  const yearLabel = year === "all" ? "All time" : year;
  const empty = data.every((m) => SERIES.every((x) => !m[x.key]));
  const Chart = kind === "bar" ? BarChart : LineChart;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 md:p-5 shadow-sm" data-testid="monthly-weight-chart">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <BarChart3 size={18} className="text-emerald-500" /> Monthly Weights
        </h2>
        <div className="flex flex-wrap items-center gap-2">
          <Toggle value={period} onChange={setPeriod} testid="monthly-period" options={[
            { v: "year", l: `Dashboard year (${yearLabel})` }, { v: "current", l: "Current year" }, { v: "rolling", l: "Last 12 months" },
          ]} />
          <Toggle value={kind} onChange={setKind} testid="monthly-kind" options={[
            { v: "bar", l: "Bars", icon: BarChart3 }, { v: "line", l: "Lines", icon: LineIcon },
          ]} />
        </div>
      </div>
      {empty ? (
        <div className="h-72 flex items-center justify-center text-sm text-slate-400" data-testid="monthly-empty">No cargo weights in this period.</div>
      ) : (
      <div className="h-72" data-testid="monthly-chart-canvas">
        <ResponsiveContainer width="100%" height="100%">
          <Chart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} width={60} tickFormatter={(v) => v.toLocaleString()} />
            <Tooltip formatter={(v) => fmt(v)} itemSorter={() => 0} />
            <Legend wrapperStyle={{ fontSize: 12 }} itemSorter={null} />
            {SERIES.map((s) => (kind === "bar"
              ? <Bar key={s.key} dataKey={s.key} name={s.name} fill={s.color} radius={[3, 3, 0, 0]} />
              : <Line key={s.key} dataKey={s.key} name={s.name} stroke={s.color} strokeWidth={2} dot={{ r: 3 }} type="monotone" />))}
          </Chart>
        </ResponsiveContainer>
      </div>
      )}
    </div>
  );
}
